// ---------------------------------------------------------------------------
// ExistingDataSetPicker — search-and-select Combobox backed by the Data
// Lake search API (via `useDatasets`).
//
// Wrapped in a DataHubClientProvider so the auth popup only fires when the
// component actually mounts — users of EvaluationTemplateForm who stay on
// the Inline/File tabs never see the Data Lake sign-in flow.
//
// Selection sends `{id, version, displayName}` upward. The parent persists
// only `id` + `version`; the displayName is cosmetic, derived from the
// dataset record at render time.
//
// This file is just the orchestrator: it owns the shared search/selection
// state and composes three presentational fields — the dataset search
// (DatasetSearchField), the version pin (DatasetVersionField), and the
// auto-configured file selector (DataSetFileFormatField).
// ---------------------------------------------------------------------------

import { Body1 } from '@fluentui/react-components';
import { useCallback, useMemo, useState } from 'react';

import {
  DataHubClientProvider,
  useDataHubClient,
} from '../../../../../context/DataHubClientContext.js';
import { useDatasets } from '../../../../../hooks/useDatasets/useDatasets.js';
import { DataHubService } from '../../../../../services/dataHubService.js';
import type { DataLakeFileSelector } from '../../EvaluationTemplateForm.types.js';

import { DataSetFileFormatField } from './DataSetFileFormatField.js';
import { DatasetSearchField } from './DatasetSearchField.js';
import { DatasetVersionField } from './DatasetVersionField.js';
import { useResolveDataset } from './useResolveDataset.js';

export interface ExistingDataSetPickerProps {
  selectedId: string | null;
  selectedVersion: number | null;
  disabled?: boolean;
  onSelect: (
    selection: {
      id: string;
      version: number | null;
      displayName: string;
    } | null,
  ) => void;
  // Data Lake sources require a fileSelector. The picker auto-detects the
  // dataset's file format and emits a single `transcript` selector; a
  // saved custom selector is shown read-only and preserved.
  fileSelector: DataLakeFileSelector[];
  onFileSelectorChange: (selectors: DataLakeFileSelector[]) => void;
}

function ExistingDataSetPickerInner({
  selectedId,
  selectedVersion,
  disabled,
  onSelect,
  fileSelector,
  onFileSelectorChange,
}: ExistingDataSetPickerProps) {
  const dataHubClient = useDataHubClient();
  const dataHubService = useMemo(
    () => new DataHubService(dataHubClient),
    [dataHubClient],
  );

  const [query, setQuery] = useState('');
  // Only published + public datasets are selectable here. `accessFilter`
  // is applied server-side (proven by the Datasets page); the Published
  // filter is applied client-side below because the search endpoint's
  // status wire format isn't verified and the server already excludes
  // Versioned (so this only drops Draft).
  const {
    datasets: allDatasets,
    isLoading,
    error,
  } = useDatasets({ query, accessFilter: 'Public' });
  const datasets = useMemo(
    () => allDatasets.filter((d) => d.status === 'Published'),
    [allDatasets],
  );

  // Track which selectedId was validated by an explicit click on an
  // option. When the user picks from the dropdown we know the dataset
  // exists — the resulting query change may return different search
  // results, but we shouldn't warn that the selection is missing.
  const [validatedId, setValidatedId] = useState<string | null>(null);

  // Resolve the currently-selected dataset for display. If the user just
  // loaded a saved template with an id that isn't on the current search
  // page, we still show "<id>" as a placeholder until it lands.
  const selected = useMemo(
    () => datasets.find((d) => d.id === selectedId),
    [datasets, selectedId],
  );

  // Seed the search box with the resolved name only if the user hasn't
  // typed anything — the resolved name shouldn't clobber an in-progress
  // search.
  const seedQuery = useCallback((name: string) => {
    setQuery((prev) => (prev === '' ? name : prev));
  }, []);

  const { resolveError, resolvedVersion } = useResolveDataset({
    selectedId,
    dataHubService,
    onSeedQuery: seedQuery,
    onValidatedIdChange: setValidatedId,
  });

  // Warn only when (a) we have an id, (b) it wasn't just selected by the
  // user via the dropdown, (c) we've finished a search, (d) the id still
  // isn't anywhere in the results, AND (e) we have no resolve error
  // (which has its own dedicated message). That narrows the warning to
  // genuine "the saved template references a missing dataset" cases.
  const showMissingWarning =
    selectedId !== null &&
    selectedId !== validatedId &&
    !isLoading &&
    selected === undefined &&
    resolveError === null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <DatasetSearchField
        datasets={datasets}
        isLoading={isLoading}
        query={query}
        onQueryChange={setQuery}
        selectedId={selectedId}
        disabled={disabled}
        resolveError={resolveError}
        showMissingWarning={showMissingWarning}
        onSelect={onSelect}
        onValidatedIdChange={setValidatedId}
      />

      <DatasetVersionField
        selected={selected}
        selectedId={selectedId}
        selectedVersion={selectedVersion}
        disabled={disabled ?? false}
        onSelect={onSelect}
      />

      {selectedId !== null && (
        <DataSetFileFormatField
          dataHubService={dataHubService}
          dataSetId={selectedId}
          version={selectedVersion ?? selected?.version ?? resolvedVersion}
          disabled={disabled ?? false}
          fileSelector={fileSelector}
          onFileSelectorChange={onFileSelectorChange}
        />
      )}

      {error !== null && (
        <Body1
          role="alert"
          style={{ color: 'var(--colorPaletteRedForeground1)' }}
        >
          {error}
        </Body1>
      )}
    </div>
  );
}

export function ExistingDataSetPicker(props: ExistingDataSetPickerProps) {
  // Wrap in the Data Lake auth provider so the popup only fires when this
  // picker actually mounts. The provider initializes lazily inside.
  return (
    <DataHubClientProvider>
      <ExistingDataSetPickerInner {...props} />
    </DataHubClientProvider>
  );
}
